import React, { useState, useMemo } from 'react';
import { Search, Calendar, Phone, MapPin, FileText, Image as ImageIcon, FileText as FileTextIcon, ArrowLeft } from 'lucide-react';
import { formatDDMMYYYY } from '../../utils/dateI18n';
import { UserType } from '../../types/index';
import ModalDetalhesACPanel from './ModalDetalhesACPanel';

/**
 * Página AC/Painel Solar - Lista cronológica de registos (apenas com origem ativa)
 */
export default function PaginaACPanelSolar({ 
  records, 
  currentUser, 
  onUpdate, 
  onDelete,
  onBack
}) {
  const [search, setSearch] = useState('');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [filterType, setFilterType] = useState('ALL'); // 'ALL', 'MOVEMENT', 'BUDGET'

  // Filtrar registos por pesquisa e tipo
  const filteredRecords = useMemo(() => {
    let filtered = records;
    
    // Filtrar por tipo (MOVEMENT, BUDGET, ou ALL)
    if (filterType !== 'ALL') {
      filtered = filtered.filter(record => record.recordType === filterType);
    }
    
    // Filtrar por pesquisa
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(record => 
        (record.client || '').toLowerCase().includes(searchLower) ||
        (record.locality || '').toLowerCase().includes(searchLower) ||
        (record.phone || '').toLowerCase().includes(searchLower) ||
        (record.invoiceNumber || '').toLowerCase().includes(searchLower) ||
        (record.notes || '').toLowerCase().includes(searchLower) ||
        (record.products && Array.isArray(record.products) 
          ? record.products.some(p => (p.name || '').toLowerCase().includes(searchLower))
          : false)
      );
    }
    
    return filtered;
  }, [records, search, filterType]);

  const handleRecordClick = (record) => {
    setSelectedRecord(record);
  };

  const handleCloseModal = () => {
    setSelectedRecord(null);
  };

  const isSimpleUser = currentUser?.userType === UserType.SIMPLE;

  return (
    <div className="max-w-6xl mx-auto space-y-6 px-4 sm:px-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          {isSimpleUser && onBack && (
            <button
              onClick={onBack}
              className="p-2 hover:bg-slate-100 rounded-xl transition-colors bg-white shadow-sm border border-slate-200"
            >
              <ArrowLeft size={24} className="text-slate-600" />
            </button>
          )}
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 tracking-tight">
              AC/ Painel Solar
            </h1>
            <p className="text-green-600 font-bold mt-1 uppercase text-[10px] tracking-widest">
              Registos de Ar Condicionado e Painéis Solares
            </p>
          </div>
        </div>
      </div>

      {/* Filtros e pesquisa */}
      <div className="space-y-4">
        {/* Botões de filtro por tipo */}
        <div className="flex gap-3">
          <button
            onClick={() => setFilterType('ALL')}
            className={`px-4 py-2 rounded-xl font-black text-sm transition-all ${
              filterType === 'ALL'
                ? 'bg-green-600 text-white shadow-md'
                : 'bg-white border-2 border-slate-200 text-slate-600 hover:border-green-300'
            }`}
          >
            Todos
          </button>
          <button
            onClick={() => setFilterType('MOVEMENT')}
            className={`px-4 py-2 rounded-xl font-black text-sm transition-all ${
              filterType === 'MOVEMENT'
                ? 'bg-green-600 text-white shadow-md'
                : 'bg-white border-2 border-slate-200 text-slate-600 hover:border-green-300'
            }`}
          >
            Serviço
          </button>
          <button
            onClick={() => setFilterType('BUDGET')}
            className={`px-4 py-2 rounded-xl font-black text-sm transition-all ${
              filterType === 'BUDGET'
                ? 'bg-green-600 text-white shadow-md'
                : 'bg-white border-2 border-slate-200 text-slate-600 hover:border-green-300'
            }`}
          >
            Orçamento
          </button>
        </div>
        
        {/* Barra de pesquisa */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
          <input
            type="text"
            placeholder="Pesquisar por cliente, localidade, telefone, produto..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-12 pr-4 py-4 bg-white border-2 border-slate-200 rounded-2xl font-bold text-slate-900 placeholder-slate-400 focus:outline-none focus:border-green-600 transition-colors"
          />
        </div>
      </div>

      {/* Lista de registos */}
      {filteredRecords.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-slate-200">
          <div className="text-slate-400 text-lg font-bold">
            {search ? 'Nenhum registo encontrado' : 'Ainda não há registos'}
          </div>
          {!search && (
            <div className="text-slate-400 text-sm mt-2">
              Os registos aparecerão aqui quando criar movimentos de serviço ligados a orçamentos de AC/Painel Solar, ou quando criar orçamentos com notas mencionando AC/Painel Solar
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredRecords.map((record) => (
            <div
              key={record.id}
              onClick={() => handleRecordClick(record)}
              className="bg-white rounded-2xl p-6 border-2 border-slate-200 hover:border-green-300 hover:shadow-lg transition-all cursor-pointer active:scale-[0.99]"
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                {/* Informações principais */}
                <div className="flex-1 space-y-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-xl font-black text-slate-900">
                          {record.client || '—'}
                        </h3>
                        {record.recordType === 'BUDGET' && (
                          <span className="px-2.5 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs font-black uppercase flex items-center gap-1">
                            <FileTextIcon size={12} />
                            Orçamento
                          </span>
                        )}
                      </div>
                      {record.locality && (
                        <div className="flex items-center gap-2 text-slate-600 text-sm font-bold">
                          <MapPin size={14} />
                          {record.locality}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-black text-green-600">
                        {record.value ? `${Number(record.value).toFixed(2)}€` : '—'}
                      </div>
                      {record.date && (
                        <div className="flex items-center gap-1 text-slate-400 text-xs font-bold mt-1 justify-end">
                          <Calendar size={12} />
                          {formatDDMMYYYY(new Date(record.date))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Produtos */}
                  {record.products && Array.isArray(record.products) && record.products.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {record.products.map((prod, idx) => (
                        <div
                          key={idx}
                          className="bg-slate-50 px-3 py-1.5 rounded-xl text-sm font-bold text-slate-700 border border-slate-200"
                        >
                          {prod.name || '—'}
                          {prod.ref && <span className="text-slate-500 ml-1">({prod.ref})</span>}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Informações adicionais */}
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    {record.phone && (
                      <div className="flex items-center gap-2 text-slate-600 font-bold">
                        <Phone size={14} />
                        {record.phone}
                      </div>
                    )}
                    {record.invoiceNumber && record.invoiceStatus === 'FATURADO' && (
                      <div className="flex items-center gap-2 text-slate-600 font-bold">
                        <FileText size={14} />
                        Fatura: {record.invoiceNumber}
                      </div>
                    )}
                    {record.photos && record.photos.length > 0 && (
                      <div className="flex items-center gap-2 text-blue-600 font-bold">
                        <ImageIcon size={14} />
                        {record.photos.length} {record.photos.length === 1 ? 'foto' : 'fotos'}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de detalhes */}
      {selectedRecord && (
        <ModalDetalhesACPanel
          record={selectedRecord}
          onClose={handleCloseModal}
          onUpdate={onUpdate}
          currentUser={currentUser}
        />
      )}
    </div>
  );
}

